import React, { createContext, useContext, useState, useEffect } from "react";
import { featureTranslations } from "@/i18n/featureTranslations";
import { pageTranslations } from "@/i18n/pageTranslations";
import { translations, type Language as BaseLanguage } from "@/i18n/translations";
import { localeToUiLanguage, uiLanguageToDefaultLocale } from "@/domain/locale/catalog";
import { supabase } from "@/integrations/supabase/client";

export type Language = BaseLanguage;

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    const saved = localStorage.getItem("orca-language");
    if (saved === "pt" || saved === "en" || saved === "es") {
      return saved;
    }

    if (typeof navigator !== "undefined") {
      const browser = navigator.language.toLowerCase();
      if (browser.startsWith("pt")) return "pt";
      if (browser.startsWith("es")) return "es";
    }

    return "en";
  });

  useEffect(() => {
    localStorage.setItem("orca-language", language);
  }, [language]);

  useEffect(() => {
    let active = true;

    const syncFromProfile = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!active || !session?.user) return;

      const { data } = await supabase
        .from("profiles")
        .select("user_locale")
        .eq("user_id", session.user.id)
        .maybeSingle();

      if (!active || !data?.user_locale) return;

      const profileLanguage = localeToUiLanguage(data.user_locale);
      setLanguageState(profileLanguage);
      localStorage.setItem("orca-language", profileLanguage);
    };

    void syncFromProfile();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(() => {
      void syncFromProfile();
    });

    const handleProfileUpdated = () => {
      void syncFromProfile();
    };
    window.addEventListener("orzio-profile-updated", handleProfileUpdated);

    return () => {
      active = false;
      subscription.unsubscribe();
      window.removeEventListener("orzio-profile-updated", handleProfileUpdated);
    };
  }, []);

  const persistUserLanguage = async (lang: Language) => {
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!session?.user) return;

    await supabase
      .from("profiles")
      .update({ user_locale: uiLanguageToDefaultLocale(lang) })
      .eq("user_id", session.user.id);
    window.dispatchEvent(new Event("orzio-profile-updated"));
  };

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem("orca-language", lang);
    void persistUserLanguage(lang);
  };

  const t = (key: string): string => {
    const pageValue = pageTranslations[language]?.[key];
    if (pageValue) return pageValue;

    const featureValue = featureTranslations[language]?.[key];
    if (featureValue) return featureValue;

    const value = translations[language]?.[key];
    if (value) return value;
    // Fallback: try other languages before showing raw key
    for (const lang of ["en", "pt", "es"] as BaseLanguage[]) {
      if (lang !== language && pageTranslations[lang]?.[key]) {
        return pageTranslations[lang][key];
      }
      if (lang !== language && featureTranslations[lang]?.[key]) {
        return featureTranslations[lang][key];
      }
      if (lang !== language && translations[lang]?.[key]) {
        return translations[lang][key];
      }
    }
    // Last resort: convert key to human-readable text
    const fallback = key.split(".").pop() || key;
    return fallback
      .replace(/([A-Z])/g, " $1")
      .replace(/[_-]/g, " ")
      .replace(/^\w/, (c) => c.toUpperCase())
      .trim();
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const createTranslator = (lang: Language) => (key: string): string => {
  const pageValue = pageTranslations[lang]?.[key];
  if (pageValue) return pageValue;
  const featureValue = featureTranslations[lang]?.[key];
  if (featureValue) return featureValue;
  const value = translations[lang]?.[key];
  if (value) return value;
  for (const fallbackLang of ["en", "pt", "es"] as BaseLanguage[]) {
    if (fallbackLang !== lang && pageTranslations[fallbackLang]?.[key]) return pageTranslations[fallbackLang][key];
    if (fallbackLang !== lang && featureTranslations[fallbackLang]?.[key]) return featureTranslations[fallbackLang][key];
    if (fallbackLang !== lang && translations[fallbackLang]?.[key]) return translations[fallbackLang][key];
  }
  const fallback = key.split(".").pop() || key;
  return fallback.replace(/([A-Z])/g, " $1").replace(/[_-]/g, " ").replace(/^\w/, (c) => c.toUpperCase()).trim();
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
